import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import {
  LayerStyle,
  ShapefileLegendData,
  ShapefileLegendGroup,
  ShapefileLegendItem
} from "types/maps";
import { isDefined } from "utils/fp";
import { buildGeoTiffColorbarItems } from "utils/geotiffLegend";

import { GeomT } from "models/model";
import { GeoTiffRenderingMode, IShapefile, ShapefileFileType } from "models/projects";

import {
  useEditableProjectLayerList,
  useProjectLayerTree
} from "components/project/layers/hooks";
import {
  ProjectTreeNodeT,
  TREE_NODE_TYPE
} from "components/project/layers/hooks/project-layer-tree/useProjectLayerTree.types";
import { useProjectContext } from "components/project/projects/context";
import {
  BinSettings,
  BinningMethod,
  binFeaturesByBinSize,
  binFeaturesByQuantile
} from "components/project/shapefiles/components/shapefile-binning/ShapefileBinning";
import { useCheckedShapefileList } from "components/project/shapefiles/hooks/useCheckedShapefileList";

const DEFAULT_COLOR = "#000000";

/** Strip trailing ` (N)` count suffix from a bin label for color matching. */
const stripCount = (label: string): string => label.replace(/\s*\(\d+\)$/, "");

const groupBy = <T>(items: readonly T[], keyFn: (item: T) => string): Map<string, T[]> =>
  items.reduce((map, item) => {
    const key = keyFn(item);
    const group = map.get(key) ?? [];
    group.push(item);
    map.set(key, group);
    return map;
  }, new Map<string, T[]>());

const getLayerStyle = (shapefile: ProjectTreeNodeT): Maybe<LayerStyle> =>
  shapefile.layer?.style
    ? {
        color: shapefile.layer.style.color,
        strokeColor: shapefile.layer.style.strokeColor,
        thickness:
          typeof shapefile.layer.style.thickness === "number"
            ? shapefile.layer.style.thickness
            : undefined
      }
    : undefined;

const buildLayerColorMap = (
  layers: { shapefileId: string; Color: string | null; StrokeColor: string | null }[]
): Map<string, { color: string; strokeColor?: string }> =>
  new Map(
    layers.map((l) => [
      l.shapefileId,
      { color: l.Color ?? DEFAULT_COLOR, strokeColor: l.StrokeColor ?? undefined }
    ])
  );

const buildBinGroups = (
  features: readonly GeomT[],
  binningSettings: BinSettings,
  binColors: Readonly<Record<string, string>>,
  layerStyle: Maybe<LayerStyle>
): readonly ShapefileLegendGroup[] | null => {
  const result =
    binningSettings.method === BinningMethod.Quantile
      ? binFeaturesByQuantile(
          features as GeomT[],
          binningSettings.property,
          binningSettings.quantileCount ?? 4
        )
      : binFeaturesByBinSize(
          features as GeomT[],
          binningSettings.property,
          binningSettings.lessThan,
          binningSettings.binSize,
          binningSettings.greaterThan
        );

  if (result.bins.length === 0) return null;

  // Build a lookup keyed by label without count suffix so colors match
  // regardless of differing counts between stored colors and current bins.
  const colorByBase = new Map(
    Object.entries(binColors).map(([k, v]) => [stripCount(k), v])
  );

  const items: ShapefileLegendItem[] = result.bins
    .filter((bin) => {
      // Match Details panel: skip bins that have no features and no configured placeholder color
      const baseLabel = stripCount(bin.label);
      return bin.features.length > 0 || colorByBase.has(baseLabel);
    })
    .map((bin) => {
      const baseLabel = stripCount(bin.label);
      const isEmpty = bin.features.length === 0;
      const placeholderColor = colorByBase.get(baseLabel);
      const resolvedColor = isEmpty
        ? placeholderColor ?? DEFAULT_COLOR
        : bin.features[0].Color || layerStyle?.color || DEFAULT_COLOR;
      const resolvedStroke = isEmpty
        ? placeholderColor ?? layerStyle?.strokeColor ?? undefined
        : bin.features[0]?.StrokeColor ?? layerStyle?.strokeColor ?? undefined;
      return {
        color: resolvedColor,
        strokeColor: resolvedStroke,
        label: baseLabel,
        count: bin.features.length
      };
    });

  return [{ heading: "", items }];
};

const buildFallbackGroups = (
  features: readonly GeomT[],
  propertyKey: string,
  layerStyle: Maybe<LayerStyle>
): readonly ShapefileLegendGroup[] => {
  const grouped = groupBy(features, (f) => String(f.Properties?.[propertyKey] ?? ""));

  const items = Array.from(grouped.values())
    .map((geoms) => {
      const first = geoms[0];
      if (!isDefined(first)) return null;
      return {
        color: first.Color || layerStyle?.color || DEFAULT_COLOR,
        strokeColor: first.StrokeColor ?? layerStyle?.strokeColor ?? undefined,
        label: String(first.Properties?.[propertyKey] ?? "Unknown"),
        count: geoms.length
      };
    })
    .filter(isDefined);

  return items.length > 0 ? [{ heading: "", items }] : [];
};

const buildDetailsGroups = (
  features: readonly GeomT[],
  propertyKey: string,
  binningSettings: Maybe<BinSettings>,
  binColors: Readonly<Record<string, string>>,
  layerStyle: Maybe<LayerStyle>
): readonly ShapefileLegendGroup[] => {
  if (binningSettings) {
    const binGroups = buildBinGroups(features, binningSettings, binColors, layerStyle);
    if (binGroups) return binGroups;
  }
  return buildFallbackGroups(features, propertyKey, layerStyle);
};

/** The GeoTIFF-specific fields a selected layer carries when it is a raster (see IShapefile). */
type GeoTiffLayer = ProjectTreeNodeT &
  Pick<IShapefile, "fileType" | "renderingMode" | "rasterAttributes">;

/** True when the selected layer is a GeoTIFF raster (vs. a vector shapefile). */
const isGeoTiffLayer = (node: ProjectTreeNodeT): node is GeoTiffLayer =>
  (node as unknown as Partial<IShapefile>).fileType === ShapefileFileType.GeoTiff;

/**
 * Legend data for a GeoTIFF: a raster has no vector features to bin, so its "details"
 * are the discrete value→color ranges, emitted as the same `[{ heading, items }]` shape
 * as shapefile details so they render identically. RGB-mode GeoTIFFs show nothing
 * (EVA-6737); an undefined rendering mode is value-based and shown. Missing
 * `rasterAttributes` → no items → renders nothing. (EVA-6644)
 */
const buildGeoTiffLegendData = (geoTiff: GeoTiffLayer): ShapefileLegendData => {
  const items: ShapefileLegendItem[] =
    geoTiff.renderingMode === GeoTiffRenderingMode.RGB
      ? []
      : buildGeoTiffColorbarItems(geoTiff.rasterAttributes).map((item) => ({
          color: item.color,
          // Match the stroke to the fill so the swatch is a solid, true-color block.
          // Without this, ShapefileLegendBase draws a default semi-transparent gray
          // border that tints the color and makes it look inconsistent with the
          // borderless swatches in the GeoTIFF layer-details panel. (EVA-6644)
          strokeColor: item.color,
          label: String(item.title),
          count: 0
        }));

  return {
    title: geoTiff.title ?? "GeoTIFF",
    groups: items.length > 0 ? [{ heading: "", items }] : [],
    hasData: items.length > 0
  };
};

/** Walk the project tree and map each shapefileId to its immediate parent folder title. */
const buildFolderMap = (tree: ProjectTreeNodeT[]): Map<string, string> => {
  const map = new Map<string, string>();

  const walk = (nodes: readonly ProjectTreeNodeT[], parentTitle: string) => {
    for (const node of nodes) {
      if (node.type === TREE_NODE_TYPE.LAYER && node.shapefileId) {
        map.set(node.shapefileId, parentTitle);
      }
      if (node.children) {
        const folderTitle =
          node.type === TREE_NODE_TYPE.FOLDER ? node.title : parentTitle;
        walk(node.children, folderTitle);
      }
    }
  };

  walk(tree, "");
  return map;
};

const buildNameGroups = (
  shapefiles: readonly {
    layerName: string;
    color: string;
    strokeColor?: string;
    strokeWidth?: number;
    shapefileId: string;
  }[],
  folderMap: Map<string, string>
): readonly ShapefileLegendGroup[] => {
  const grouped = new Map<string, ShapefileLegendItem[]>();

  for (const sf of shapefiles) {
    const folder = folderMap.get(sf.shapefileId) ?? "";
    const items = grouped.get(folder) ?? [];
    items.push({
      color: sf.color,
      strokeColor: sf.strokeColor,
      strokeWidth: sf.strokeWidth,
      label: sf.layerName,
      count: 0
    });
    grouped.set(folder, items);
  }

  const groups: ShapefileLegendGroup[] = [];
  for (const [heading, items] of grouped) {
    groups.push({ heading, items });
  }
  return groups;
};

/**
 * Builds legend items for both Details and Name legend modes.
 */
export function useShapefileLegendItems(showDetails: boolean): ShapefileLegendData {
  const selectedShapefile = useSelector(
    (state: RootState) => state.project.selectedShapefile
  ) as Maybe<ProjectTreeNodeT>;
  const shapefileFeatures = useSelector(
    (state: RootState) => state.project.shapefileFeatures
  );
  const selectedFeatureProperty = useSelector(
    (state: RootState) => state.project.selectedFeatureProperty
  );
  const shapefileProperties = useSelector(
    (state: RootState) => state.project.shapefileProperties
  );

  const { selectedProjectId } = useProjectContext();
  const layerList = useEditableProjectLayerList({ projectId: selectedProjectId });
  const checkedShapefileList = useCheckedShapefileList();
  const { tree } = useProjectLayerTree();

  return useMemo(() => {
    if (showDetails && isDefined(selectedShapefile)) {
      if (isGeoTiffLayer(selectedShapefile)) {
        return buildGeoTiffLegendData(selectedShapefile);
      }

      const id = selectedShapefile.projectShapefileId ?? selectedShapefile.shapefileId;
      const features = shapefileFeatures?.[id]?.features ?? [];
      const propertyKey = selectedFeatureProperty?.[selectedShapefile.key] ?? "NAME";

      const storedProps = shapefileProperties?.[selectedShapefile.key]?.[propertyKey];
      const binningSettings = storedProps?.binningSettings ?? null;
      const binColors: Record<string, string> = storedProps?.placeholderBinColors ?? {};
      const layerStyle = getLayerStyle(selectedShapefile);

      const groups = buildDetailsGroups(
        features,
        propertyKey,
        binningSettings,
        binColors,
        layerStyle
      );
      return {
        title: selectedShapefile.title
          ? `${selectedShapefile.title} — ${propertyKey}`
          : propertyKey,
        groups,
        hasData: groups.length > 0 && groups.some((g) => g.items.length > 0)
      };
    }

    // Name legend mode — use layer-level style colors (matching the Layers panel)
    const layerColorMap = buildLayerColorMap(layerList.data ?? []);
    const folderMap = buildFolderMap(tree);

    const shapefiles = (checkedShapefileList.data ?? [])
      // AC2 (EVA-6737): an RGB-mode GeoTIFF must not appear in ANY legend. In RGB mode
      // colours are raw R/G/B band values, not a mapped data range, so a single name
      // swatch would misrepresent the raster. Filter here, on the raw checked list,
      // because the `.map()` below drops `fileType`/`renderingMode`. Value-based
      // GeoTIFFs are intentionally kept — they list by name like shapefiles. (EVA-6644)
      .filter(
        (sf) =>
          !(
            sf.fileType === ShapefileFileType.GeoTiff &&
            sf.renderingMode === GeoTiffRenderingMode.RGB
          )
      )
      .map((sf) => {
        const layerStyle = layerColorMap.get(sf.shapefileId);
        return {
          layerName: sf.layerName,
          shapefileId: sf.shapefileId,
          color: layerStyle?.color ?? DEFAULT_COLOR,
          strokeColor: layerStyle?.strokeColor ?? undefined,
          strokeWidth: sf.features?.[0]?.thickness ?? undefined
        };
      });

    const groups = buildNameGroups(shapefiles, folderMap);
    return {
      title: "Shapefile & GeoTIFF",
      groups,
      hasData: groups.length > 0 && groups.some((g) => g.items.length > 0)
    };
  }, [
    showDetails,
    selectedShapefile,
    shapefileFeatures,
    selectedFeatureProperty,
    shapefileProperties,
    checkedShapefileList.data,
    layerList.data,
    tree
  ]);
}
